import { InvalidUrlError } from '@execonline-inc/url';
import { Maybe, nothing } from 'maybeasy';
import { AppyError } from '../../../../../../Appy';
import { MissingLinkError } from '../../../../../../LinkyLinky';
import { Resource } from '../../../../../../Resource/Types';
import { TPlainTextKey } from '../../../../../../Translations';
import { SelectableUserGroup } from '../../../../../Form/GroupInputWithSuggestions/Types';
import { ShareUrlResource } from '../../../../Types';

export interface License {
  id: number;
  licenseTypeName: string;
  quantityAvailable: number;
  startOn: Date;
  endOn: Date;
}

export type UnlicensedAccessOption = 'block' | 'read-only' | 'allocate';

export interface LicenseOption {
  id: number;
  licenseName: Maybe<string>;
  licenseTypeName: string;
  quantityAvailable: number;
  startOn: Date;
  endOn: Date;
}

export type ProgramKind = 'on-demand' | 'scheduled';

export interface ProgramFamily {
  id: number;
  name: string;
  // The platform's offering type (epc, aep, coaching, ...), left open so a new
  // one lands in a sensible bucket rather than failing to decode.
  offeringType: string;
  schoolName: Maybe<string>;
}

export interface Program {
  id: number;
  name: string;
  kind: ProgramKind;
  startOn: Date;
  programFamily: ProgramFamily;
}

export interface LearningCollection {
  id: number;
  name: string;
  publishedAt: Maybe<Date>;
  license: Maybe<License>;
  shareUrl: Maybe<ShareUrlResource>;
  showUnlicensedContent: boolean;
  productLicenseId: Maybe<number>;
  availableLicenses: ReadonlyArray<LicenseOption>;
  programs: ReadonlyArray<Program>;
}

export type LearningCollectionResource = Resource<LearningCollection>;

export type ParticipantStatus = 'verified' | 'not-found' | 'pending' | 'unlicensed';

export interface UnverifiedUserParticipant {
  kind: 'user';
  email: string;
  status: 'pending';
}

export interface UnverifiedGroupParticipant {
  kind: 'group';
  name: string;
  status: 'pending' | 'not-found';
}

export type UnverifiedParticipant = UnverifiedUserParticipant | UnverifiedGroupParticipant;

export interface VerifiedUserParticipant {
  kind: 'user';
  id: number;
  email: string;
  name: string;
  status: 'verified';
  licenses: ReadonlyArray<License>;
}

export interface NotFoundUserParticipant {
  kind: 'user';
  email: string;
  name: string;
  status: 'not-found';
}

export interface UnlicensedUserParticipant {
  kind: 'user';
  id: number;
  email: string;
  name: string;
  status: 'unlicensed';
  licenses: ReadonlyArray<License>;
}

export type UserParticipant =
  | VerifiedUserParticipant
  | NotFoundUserParticipant
  | UnlicensedUserParticipant;

export interface GroupMember {
  id: number;
}

export interface GroupParticipant {
  kind: 'group';
  id: number;
  name: string;
  members: ReadonlyArray<GroupMember>;
  status: ParticipantStatus;
}

export type VerifiedParticipant = VerifiedUserParticipant | GroupParticipant;
export type Participant =
  | VerifiedParticipant
  | UnverifiedParticipant
  | NotFoundUserParticipant
  | UnlicensedUserParticipant;
export type Participants = ReadonlyArray<Participant>;

export type ShareBy = 'email' | 'url';
export type ShareType = 'collection' | 'program';

// State for program-specific ShareUrl fetching
export type ProgramShareUrlState =
  | { kind: 'idle' }
  | { kind: 'loading'; programFamilyId: number }
  | { kind: 'loaded'; shareUrlResource: ShareUrlResource }
  | { kind: 'error'; programFamilyId: number };

// Derived state for the share URL display component
export type ShareUrlState =
  | { kind: 'no-url' }
  | { kind: 'ready'; href: string }
  | { kind: 'loading'; href: string }
  | { kind: 'error'; href: string };

export const noShareUrl = (): ShareUrlState => ({ kind: 'no-url' });
export const shareUrlReady = (href: string): ShareUrlState => ({ kind: 'ready', href });
export const shareUrlLoading = (href: string): ShareUrlState => ({ kind: 'loading', href });
export const shareUrlError = (href: string): ShareUrlState => ({ kind: 'error', href });

// How the license review was opened (DVLP-8042). Confirm is the step before
// sending, so the review offers Send. The banner's "View details" is a look at
// who is missing what while the list is still being built, so it does not.
export type ReviewEntry = 'confirm' | 'license-alert';

interface BaseState {
  reviewEntry: ReviewEntry;
  learningCollectionResource: LearningCollectionResource;
  shareBy: ShareBy;
  shareType: ShareType;
  programId: Maybe<number>;
  programShareUrlState: ProgramShareUrlState;
  participants: Participants;
  userGroup: SelectableUserGroup;
  scheduleEnabled: boolean;
  scheduleDate: Date;
  scheduleTime: Date;
  scheduleTimezone: string;
  scheduleModified: boolean; // Track if user has modified the schedule
  unlicensedAccess: UnlicensedAccessOption;
  selectedLicenseId: Maybe<number>;
  advancedSettingsModified: boolean;
}

export interface Waiting {
  kind: 'waiting';
}

export interface Loading {
  kind: 'loading';
  learningCollectionId: number;
  shareBy: ShareBy;
  shareType: ShareType;
  programId: Maybe<number>;
}

export interface Ready extends BaseState {
  kind: 'ready';
}

export interface Searching extends BaseState {
  kind: 'searching';
}

export interface Reviewing extends BaseState {
  kind: 'reviewing';
}

export interface Refreshing extends BaseState {
  kind: 'refreshing';
}

export interface Publishing extends BaseState {
  kind: 'publishing';
}

export interface Submitting extends BaseState {
  kind: 'submitting';
}

export interface SuccessfulParticipant {
  userId: number;
  email: string;
  name: string;
  status: 'added';
  invitationId: number;
}

export interface FailedParticipant {
  userId: Maybe<number>;
  email: Maybe<string>;
  error: string;
  status: 'failed';
}

export interface ShareLearningCollectionResponse {
  successfulParticipants: ReadonlyArray<SuccessfulParticipant>;
  failedParticipants: ReadonlyArray<FailedParticipant>;
}

export interface Confirming extends BaseState {
  kind: 'confirming';
  response: ShareLearningCollectionResponse;
}

export interface Error {
  kind: 'error';
  message: TPlainTextKey;
}

export type ShareState =
  | Waiting
  | Loading
  | Ready
  | Searching
  | Reviewing
  | Refreshing
  | Publishing
  | Submitting
  | Confirming
  | Error;

// States that have programShareUrlState (all states extending BaseState)
export type ActiveShareState =
  | Ready
  | Searching
  | Reviewing
  | Refreshing
  | Publishing
  | Submitting
  | Confirming;

export const isActiveShareState = (state: ShareState): state is ActiveShareState =>
  state.kind === 'ready' ||
  state.kind === 'searching' ||
  state.kind === 'reviewing' ||
  state.kind === 'refreshing' ||
  state.kind === 'publishing' ||
  state.kind === 'submitting' ||
  state.kind === 'confirming';

export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const loading = (
  learningCollectionId: number,
  shareBy: ShareBy,
  shareType: ShareType,
  programId: Maybe<number>,
): Loading => ({
  kind: 'loading',
  learningCollectionId,
  shareBy,
  shareType,
  programId,
});

export const ready = (
  learningCollectionResource: LearningCollectionResource,
  shareBy: ShareBy,
  shareType: ShareType,
  programId: Maybe<number>,
): Ready => {
  const now = new Date();
  const learningCollection = learningCollectionResource.payload;

  // Determine initial unlicensedAccess value from learning collection
  let unlicensedAccess: UnlicensedAccessOption = 'block';
  let selectedLicenseId: Maybe<number> = nothing();

  if (learningCollection.productLicenseId.isJust()) {
    unlicensedAccess = 'allocate';
    selectedLicenseId = learningCollection.productLicenseId;
  } else if (learningCollection.showUnlicensedContent) {
    unlicensedAccess = 'read-only';
  }

  return {
    kind: 'ready',
    reviewEntry: 'confirm',
    learningCollectionResource,
    shareBy,
    shareType,
    programId,
    programShareUrlState: { kind: 'idle' },
    participants: [],
    userGroup: { kind: 'unselected' },
    scheduleEnabled: false,
    scheduleDate: now,
    scheduleTime: now,
    scheduleTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    scheduleModified: false,
    unlicensedAccess,
    selectedLicenseId,
    advancedSettingsModified: false,
  };
};

export const searching = (state: Ready): Searching => {
  return {
    ...state,
    kind: 'searching',
  };
};

export const reviewing = (state: Ready, reviewEntry: ReviewEntry): Reviewing => {
  return {
    ...state,
    kind: 'reviewing',
    reviewEntry,
  };
};

export const refreshing = (state: Reviewing): Refreshing => {
  return {
    ...state,
    kind: 'refreshing',
  };
};

export const publishing = (state: Ready): Publishing => {
  return {
    ...state,
    kind: 'publishing',
  };
};

export const submitting = (state: Reviewing): Submitting => {
  return {
    ...state,
    kind: 'submitting',
  };
};

export const confirming = (
  state: Submitting,
  response: ShareLearningCollectionResponse,
): Confirming => {
  return {
    ...state,
    kind: 'confirming',
    response,
  };
};

export const error = (message: TPlainTextKey): Error => {
  return {
    kind: 'error',
    message,
  };
};

export type ShareLearningCollectionError = MissingLinkError | AppyError | InvalidUrlError | Error;
